import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Container } from "../components/ui-kit";

const GEO = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cg fill='none' stroke='%23C99A2E' stroke-width='1'%3E%3Cpath d='M70 8 84 56 132 70 84 84 70 132 56 84 8 70 56 56Z'/%3E%3Ccircle cx='70' cy='70' r='34'/%3E%3C/g%3E%3C/svg%3E")`;
const CATEGORIES = ["All", "Charity", "Mercy", "Humanity", "Justice", "Community"];

export default function AayatLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/aayat").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const play = (v) => {
    if (!v.audio) return;
    if (current?.ref === v.ref) {
      if (playing) audioRef.current.pause();
      else audioRef.current.play().catch(() => {});
      return;
    }
    setCurrent(v);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.src = v.audio;
        audioRef.current.play().catch(() => {});
      }
    }, 40);
  };

  const filtered = cat === "All" ? items : items.filter((v) => v.category === cat);

  return (
    <div data-testid="aayat-library-page">
      <audio ref={audioRef} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />

      {/* HERO */}
      <section className="relative overflow-hidden splash-bg py-24 sm:py-28">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: GEO, backgroundSize: "150px 150px" }} />
        <div className="si-bloom pointer-events-none absolute left-1/2 top-0 h-[420px] w-[420px] -translate-x-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,154,46,0.20) 0%, transparent 62%)" }} />
        <Container className="relative text-center text-white">
          <div className="inline-flex items-center gap-2.5">
            <span className="h-px w-8 bg-gold" />
            <span className="text-xs font-semibold uppercase tracking-[0.26em] text-gold-light">The Aayat Library</span>
            <span className="h-px w-8 bg-gold" />
          </div>
          <h1 className="mt-5 font-display text-4xl font-bold sm:text-5xl lg:text-6xl">Verses of Mercy &amp; Giving</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-emerald-50/80 sm:text-lg">
            Listen to the words that inspire our purpose — foundational Qur'anic verses on charity, mercy, humanity and community, with authentic recitation and English translation.
          </p>
        </Container>
      </section>

      {/* LIBRARY */}
      <section className="bg-sand">
        <Container className="py-14 lg:py-20">
          <div className="flex flex-wrap gap-2" data-testid="aayat-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`aayat-cat-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`rounded-full border px-5 py-2 text-sm font-semibold transition ${cat === c ? "border-emerald-900 bg-emerald-900 text-white" : "border-emerald-900/15 text-emerald-900 hover:border-gold hover:text-gold"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-800" /></div>
          ) : filtered.length === 0 ? (
            <p className="py-24 text-center text-slate-500">No verses available right now. Please check back soon.</p>
          ) : (
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {filtered.map((v, i) => {
                const isCur = current?.ref === v.ref;
                return (
                  <motion.div
                    key={v.ref}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05, duration: 0.5 }}
                    className={`relative flex flex-col overflow-hidden rounded-3xl border bg-white p-7 transition-all duration-300 ${isCur ? "border-gold hh-shadow" : "border-emerald-900/10 hover:-translate-y-1 hover:border-gold/40 hover:hh-shadow"}`}
                    data-testid={`aayat-card-${v.ref}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">{v.category}</span>
                      <span className="text-xs font-medium text-slate-400">{v.surah} · {v.ayah}</span>
                    </div>
                    <p dir="rtl" className="mt-5 text-2xl leading-[2] text-emerald-950 sm:text-[28px]" style={{ fontFamily: "'Amiri', serif" }}>{v.arabic}</p>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">"{v.translation}"</p>
                    <div className="mt-6 flex items-center gap-3">
                      <button
                        onClick={() => play(v)}
                        disabled={!v.audio}
                        data-testid={`aayat-play-${v.ref}`}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {isCur && playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        {isCur && playing ? "Pause" : "Listen"}
                      </button>
                      <span className="text-xs font-semibold uppercase tracking-widest text-gold">Qur'an {v.ref}</span>
                    </div>
                    {isCur && <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-gold to-gold-light" />}
                  </motion.div>
                );
              })}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}
